// Preprocesses `@if` condition operands before boolean tokenization.
// Reuses the `${...}` expression evaluator for var-calls and arithmetic.
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import { findTopLevelComparison, splitLogicalOperators, unwrapWrappedParentheses } from './if-condition-preprocess-scan'
import { evaluateLoopPlaceholderExpression } from './placeholder-expression'

// Rewrite condition operands so boolean parsing sees resolved comparison values.
export function preprocessConditionExpression(
  source: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): string {
  const logical = splitLogicalOperators(source)
  if (logical.operators.length === 0) {
    // Return the preprocessed single condition segment.
    return preprocessConditionSegment(source, scope, variables, objectVariables, arrays)
  }

  let output = preprocessConditionSegment(logical.segments[0], scope, variables, objectVariables, arrays)
  // Merge logical operators back around the rewritten condition segments.
  for (let index = 0; index < logical.operators.length; index += 1) {
    output += ` ${logical.operators[index]} ${preprocessConditionSegment(logical.segments[index + 1], scope, variables, objectVariables, arrays)}`
  }

  // Return the full preprocessed condition string.
  return output
}

// Rewrite one condition segment as comparison, boolean group, or standalone operand.
function preprocessConditionSegment(
  source: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): string {
  const trimmed = source.trim()
  if (!trimmed) return trimmed

  const unwrapped = unwrapWrappedParentheses(trimmed)
  if (unwrapped !== null && hasTopLevelBooleanSyntax(unwrapped)) {
    // Keep boolean grouping while recursively rewriting the nested condition.
    return `(${preprocessConditionExpression(unwrapped, scope, variables, objectVariables, arrays)})`
  }

  const comparison = findTopLevelComparison(trimmed)
  if (!comparison) {
    // Return one rewritten standalone operand token.
    return resolveConditionOperand(trimmed, scope, variables, objectVariables, arrays)
  }

  const left = resolveConditionOperand(trimmed.slice(0, comparison.start), scope, variables, objectVariables, arrays)
  const right = resolveConditionOperand(trimmed.slice(comparison.end), scope, variables, objectVariables, arrays)
  // Return the rewritten comparison segment.
  return `${left} ${comparison.operator} ${right}`
}

// Resolve one raw condition operand through the shared placeholder-expression evaluator.
function resolveConditionOperand(
  source: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): string {
  const trimmed = source.trim()
  if (!trimmed || isQuotedLiteral(trimmed)) return trimmed
  const resolved = evaluateLoopPlaceholderExpression(trimmed, scope, variables, objectVariables, arrays)
  // Return the evaluated operand when compile-time resolution succeeds.
  return resolved ?? trimmed
}

// Detect whether nested source still contains boolean grammar after unwrapping.
function hasTopLevelBooleanSyntax(source: string): boolean {
  return splitLogicalOperators(source).operators.length > 0 || findTopLevelComparison(source) !== null
}

// Check whether a token is wrapped by matching single or double quotes.
function isQuotedLiteral(value: string): boolean {
  return (value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))
}
